import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { RelationTypeDropdown } from "Components/Shared/SelectorsForm/RelationTypeDropdown";
import { RelationType } from "Infrastructure/Api/Api";
import {
  OTHER_ROLES,
  type RoleCategory,
  getRoleCategoryForRelationType,
} from "Models/BusinessRelationTypes";
import { useState } from "react";
import type {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
} from "react-hook-form";
import { useController } from "react-hook-form";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Form.RoleCategory;

type Props<FormType extends FieldValues> = {
  name: FieldPath<FormType>;
  control: Control<FormType>;
  label?: string;
};

export const GroupedRelationTypeSelector = <T extends FieldValues>({
  control,
  name,
  label,
}: Props<T>) => {
  const { t } = useResource();
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ control, name });

  const derivedCategory = value
    ? getRoleCategoryForRelationType(value as RelationType)
    : undefined;

  // Only a saved role from the "other" group starts on "Other" — a saved statutory body or proxy
  // holder has its own radio and must not open the specific-role picker.
  const [isOtherSelected, setIsOtherSelected] = useState(
    derivedCategory === "other",
  );
  const selectedCategory = isOtherSelected ? "other" : (derivedCategory ?? "");

  const handleCategoryChange = (category: RoleCategory) => {
    setIsOtherSelected(false);
    if (category === "statutoryBody") {
      onChange(RelationType.StatutoryBody as PathValue<T, typeof name>);
      return;
    }

    if (category === "proxyHolder") {
      onChange(RelationType.ProxyHolder as PathValue<T, typeof name>);
      return;
    }

    if (!value || !OTHER_ROLES.includes(value as RelationType)) {
      onChange(null as PathValue<T, typeof name>);
      setIsOtherSelected(true);
      return;
    }
  };

  return (
    <>
      <FormControl error={!!error}>
        {label && (
          <FormLabel
            sx={{
              fontSize: 14,
              fontWeight: 400,
              mb: "4px",
              color: "text.primary",
              "&.Mui-focused": { color: "text.primary" },
            }}
          >
            {label}
          </FormLabel>
        )}
        <RadioGroup
          value={selectedCategory}
          onChange={(_, val) => handleCategoryChange(val as RoleCategory)}
        >
          <FormControlLabel
            value="statutoryBody"
            control={<Radio />}
            label={
              <>
                {t(RESOURCES.StatutoryBody)}
                <Typography variant="body2" color="text.secondary">
                  {t(RESOURCES.StatutoryBodyHint)}
                </Typography>
              </>
            }
          />
          <FormControlLabel
            value="proxyHolder"
            control={<Radio />}
            label={t(RESOURCES.ProxyHolder)}
          />
          <FormControlLabel
            value="other"
            control={<Radio />}
            label={t(RESOURCES.Other)}
          />
        </RadioGroup>
      </FormControl>
      {isOtherSelected && (
        <RelationTypeDropdown
          name={name}
          control={control}
          label={t(RESOURCES.SpecificRole)}
          allowedRelations={OTHER_ROLES}
        />
      )}
    </>
  );
};
